<script setup>
import { computed } from 'vue';
import ResultNotfound from './ResultNotfound.vue';
import Loader from '@/components/Loader.vue';

const props = defineProps({
  searchQuery: String,
  items: {
    type: Array,
    default: () => [],
  },
  status: {
    type: String,
    default: 'loading',
  },
  filters: {
    type: Array,
    default: () => [],
  },
  activeFilter: String,
  page: Number,
  totalItems: Number,
  limit: {
    type: Number,
    default: 12,
  },
  showFilters: {
    type: Boolean,
    default: false,
  },
  cardComponent: {
    type: Object,
    required: true,
  },
  setFilter: {
    type: Function,
    default: () => {},
  },
  fetchItems: {
    type: Function,
    default: () => {},
  },
  routePath: {
    type: String,
    required: true,
    default: '/blogs-and-insights',
  },
  showReadTime: {
    type: Boolean,
    required: false,
    default: true,
  },
  notFoundTitle: {
    type: String,
    default: 'No result found',
  },
  notFoundHint: {
    type: String,
    default: 'Please try again with another keywords or maybe use generic term',
  },
});

const emit = defineEmits(['update:page', 'clearSearchInput']);

const totalPages = computed(() => Math.ceil(props.totalItems / props.limit));

const onPageChange = (newPage) => {
  emit('update:page', newPage);
  console.log('page value: ', props.page);
  props.fetchItems();
};
</script>

<template>
  <div class="blog-list-section">
    <!-- Filter Tabs or Search Results Title -->
    <div v-if="showFilters && !searchQuery" class="filter-tabs">
      <button
        v-for="filter in filters"
        :key="filter.id"
        :class="['tab', { active: activeFilter === filter.id }]"
        @click="setFilter(filter.id)"
      >
        {{ filter.label }}
      </button>
    </div>
    <h2 v-else-if="searchQuery" class="search-results-title">Search Results</h2>

    <!-- Card Grid or Not Found -->
    <div class="card-container">
      <div v-if="status === 'loading'"><Loader /></div>
      <div v-else-if="status === 'error'" class="error">
        An error occurred
        <button @click="fetchItems" class="retry-btn">Retry</button>
      </div>
      <v-row v-else-if="items.length > 0" justify="center">
        <v-col
          v-for="item in items"
          class="card-list"
          :key="item.id"
          :cols="12"
          :md="5"
          :sm="8"
          :lg="4"
          :xs="12"
        >
          <component
            :is="cardComponent"
            :item="item"
            :routePath="routePath"
            :showReadTime="showReadTime"
            :activeFilter="activeFilter"
          />
        </v-col>
      </v-row>
      <ResultNotfound
        v-else-if="items.length === 0"
        :searchQuery="searchQuery"
        :title="notFoundTitle"
        :hint="notFoundHint"
        @clearSearchInput="$emit('clearSearchInput')"
      />

      <!-- Pagination -->
      <div
        v-if="items.length > 0 && totalItems > limit"
        class="pagination-container"
      >
        <v-pagination
          :value="page"
          :length="totalPages"
          active-color="#007AB2"
          @update:modelValue="onPageChange"
        ></v-pagination>
      </div>
    </div>
  </div>
</template>

<style scoped>
.blog-list-section {
  margin: 0 auto;
  padding: 4rem 2rem;
  background-color: #f9f9f9;
}

.card-container {
  max-width: 1280px;
  margin: 0 auto;
}
.card-list{
  display: flex;
  justify-content: center;
}
.filter-tabs {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  justify-content: center;
  overflow-x: auto;
}

.filer-title {
  min-width: fit-content;
}
.tab {
  background-color: transparent;
  border: none;
  font-size: 1em;
  color: #666;
  padding: 8px 16px;
  cursor: pointer;
  transition: color 0.3s;
  width: fit-content;
}

.tab.active {
  background-color: #ffe6e6;
  color: #ff4d4f;
  border-radius: 16px;
}

.tab:hover {
  color: #333;
}

.search-results-title {
  margin: 0 auto;
  background-color: transparent;
  border: none;
  font-size: 1em;
  width: fit-content;
  padding: 8px 16px;
  cursor: pointer;
  transition: color 0.3s;
  background-color: #ffe6e6;
  color: #ff4d4f;
  border-radius: 16px;
  margin-bottom: 2rem;
}

.loading,
.error {
  text-align: center;
  font-size: 1.2em;
  margin: 20px 0;
}

.retry-btn {
  margin-top: 10px;
  padding: 8px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.retry-btn:hover {
  background-color: #0056b3;
}

.pagination-container {
  text-align: center;
  margin-top: 20px;
}

.v-pagination__item--is-active {
  background-color: red !important;
}
@media screen and (max-width: 1475px) {
  .blog-list-section {
    padding: 4rem 4rem 4rem 6rem;
  }
  
}
@media screen and (max-width: 1370px) {
  .blog-list-section {
    padding: 4rem 1rem 4rem 6rem;
  }
}
@media screen and (max-width: 1280px) {
  .blog-list-section {
    padding: 4rem 4rem 4rem 5rem;
  }
}
@media screen and (max-width: 1100px) {
  .blog-list-section {
    padding: 4rem;
  }
}
@media screen and (max-width: 800px) {
  .blog-list-section {
    padding: 4rem 3rem;
  }
}

@media screen and (max-width: 730px) {
  .filter-tabs {
    display: none;
    gap: 20px;
    margin-bottom: 30px;
    justify-content: center;
  }
  .blog-list-section {
    padding: 4rem 2rem;
  }
}
@media screen and (max-width: 490px) {
  .blog-list-section {
    padding: 4rem 1rem;
  }
}
</style>
